import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import "./whatsapp.css";
import moment from "moment";
import { Spin } from "antd";
import { Anchorme } from "react-anchorme";
import logger, { Logger } from '../../utils/logger';
import { findMediaGroups, getMediaGroupId } from './utils/mediaGroupingUtil';
import MediaGroup from './utils/MediaGroup';
import toast from "react-hot-toast";

// Redux imports
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import {
  fetchMessages,
  selectMessagesByDate,
  selectMessagesStatus,
  selectReplyContext,
  setReplyContext,
  sendReaction,
  selectLead,
  Message,
  // ✅ NEW: Add pagination selectors
  fetchOlderMessages,
  selectHasMoreMessages,
  selectLoadingOlderMessages
} from "../../store/slices/messagesSlice";
import {
  selectSelectedConversation
} from "../../store/slices/conversationsSlice";
import {
  cacheMedia,
  selectMediaCache,
  selectIsMediaCached,
  openMediaPreview
} from "../../store/slices/mediaSlice";

// Component imports
import { DateDivider } from "./components";
import { MessageBubbleRedux } from "./components/MessageBubbleRedux";

// ✅ NEW: Import scroll hook
import { useScrollToTop } from "../../hooks/useScrollToTop";

interface IncomingMsgReduxProps {
  scrollToBottom: () => void;
}

const IncomingMsgRedux: React.FC<IncomingMsgReduxProps> = ({ scrollToBottom }) => {
  // Redux state and dispatch
  const dispatch = useAppDispatch();
  const selectedConversation = useAppSelector(selectSelectedConversation);
  const messagesByDate = useAppSelector(selectMessagesByDate);
  const messagesStatus = useAppSelector(selectMessagesStatus);
  const replyContext = useAppSelector(selectReplyContext);
  const lead = useAppSelector(selectLead);
  const mediaCache = useAppSelector(selectMediaCache);
  
  // ✅ NEW: Add pagination selectors
  const hasMoreMessages = useAppSelector(selectHasMoreMessages);
  const loadingOlderMessages = useAppSelector(selectLoadingOlderMessages);
  
  // Local state for media downloads
  const [loadingMedia, setLoadingMedia] = useState<{[key: string]: boolean}>({});
  const [downloadErrors, setDownloadErrors] = useState<{[key: string]: string}>({});
  
  const [previousMessageCount, setPreviousMessageCount] = useState(0);


  // Track component mount state for async operations
  const [isMounted, setIsMounted] = useState(true);

  // ✅ NEW: Add scroll hook for loading older messages
  const scrollRef = useScrollToTop({
    threshold: 100, // Load when 100px from top
    onScrollToTop: () => {
      if (selectedConversation?.convoId) {
        console.log('🔄 User scrolled to top, loading older messages...');
        dispatch(fetchOlderMessages(selectedConversation.convoId));
      }
    },
    isLoading: loadingOlderMessages,
    hasMore: hasMoreMessages
  });

  // Logging for debugging
  useEffect(() => {
    Logger.log("IncomingMsgRedux rendered", {
      conversationId: selectedConversation?.convoId,
      messagesStatus,
      hasReplyContext: !!replyContext,
      hasMoreMessages, // ✅ NEW: Log pagination state
      loadingOlderMessages
    });
    
    // Set component as mounted
    setIsMounted(true);
    
    // Cleanup on unmount
    return () => {
      setIsMounted(false);
    };
  }, [selectedConversation, messagesStatus, replyContext, hasMoreMessages, loadingOlderMessages]);

  // Fetch messages when conversation changes
  useEffect(() => {
    if (selectedConversation?.convoId) {
      Logger.log("Fetching messages for conversation:", selectedConversation.convoId);
      dispatch(fetchMessages(selectedConversation.convoId));
    }
  }, [selectedConversation?.convoId, dispatch]);

 useEffect(() => {
  const currentMessageCount = Object.keys(messagesByDate).reduce((acc, date) => 
    acc + Object.keys(messagesByDate[date]).reduce((acc2, time) => 
      acc2 + messagesByDate[date][time].length, 0), 0);

  if (currentMessageCount > 0) {
    const countDifference = currentMessageCount - previousMessageCount;
    
    console.log('🔥 MESSAGE COUNT CHECK:', {
      previous: previousMessageCount,
      current: currentMessageCount,
      difference: countDifference
    });
    
    // Only scroll if:
    // 1. First load (previousMessageCount === 0) 
    // 2. Small increase (1-5 new messages = normal chat activity)
    // DON'T scroll if large increase (20 messages = older messages loaded)
    const shouldScroll = previousMessageCount === 0 || (countDifference > 0 && countDifference <= 5);
    
    if (shouldScroll) {
      Logger.log("Messages changed, scrolling to bottom", {
        messageCount: currentMessageCount,
        reason: previousMessageCount === 0 ? 'initial_load' : 'new_messages'
      });
      
      setTimeout(() => {
        scrollToBottom();
      }, 300);
    } else {
      Logger.log("Messages changed, NOT scrolling (older messages loaded)", {
        messageCount: currentMessageCount,
        countDifference
      });
    }
    
    // Update the previous count
    setPreviousMessageCount(currentMessageCount);
  }
}, [messagesByDate, scrollToBottom, previousMessageCount]);

  // Media download handler with Redux integration
  const handleMediaDownload = useCallback(async (message: { _id: any; message: { id: any; mime_type: string | undefined; }; messageType: string; }) => {
    if (!message._id) {
      Logger.log("IncomingMsgRedux - Cannot download media - missing message ID");
      return Promise.reject(new Error("Missing message ID"));
    }
    
    Logger.log("IncomingMsgRedux - Starting media download for:", message._id);
    
    // Update loading state
    setLoadingMedia(prev => ({ ...prev, [message._id]: true }));
    setDownloadErrors(prev => ({ ...prev, [message._id]: "" }));
    
    try {
      // Use the imported service directly
      const { downloadChatMedia } = await import("../../services/whatsapp");
      
      let mediaId = message.message?.id;
      if (!mediaId) {
        Logger.log("IncomingMsgRedux - Media ID not found in message:", message);
        throw new Error("Media ID not found");
      }
      
      Logger.log("IncomingMsgRedux - Using mediaId:", mediaId);
      let formdata = new FormData();
      formdata.append("mediaId", mediaId);
      
      const response = await downloadChatMedia(formdata);
      Logger.log("IncomingMsgRedux - Download response received", { 
        hasData: !!response.data 
      });
      
      if (response.data) {
        const blob = new Blob([response.data], {
          type: message.messageType === "document"
            ? "application/pdf"
            : message.message.mime_type,
        });
        
        const url = URL.createObjectURL(blob);
        Logger.log("IncomingMsgRedux - Created blob URL:", url);
        
        // Cache in Redux
        dispatch(cacheMedia({
          messageId: message._id,
          url,
          type: message.messageType || 'unknown',
          mimeType: message.message?.mime_type || 'application/octet-stream'
        }));
        
        Logger.log("IncomingMsgRedux - Media cached in Redux for:", message._id);
        return Promise.resolve();
      }
      
      throw new Error("No data in response");
    } catch (error) {
      Logger.log("IncomingMsgRedux - Error downloading media:", error);
      setDownloadErrors(prev => ({ 
        ...prev, 
        [message._id]: "Failed to download media" 
      }));
      return Promise.reject(error);
    } finally {
      setLoadingMedia(prev => ({ ...prev, [message._id]: false }));
    }
  }, [dispatch]);

  // Handle reaction to messages with Redux
  const handleReaction = useCallback((messageId: string, emoji: string) => {
    if (!selectedConversation) {
      Logger.log("Cannot send reaction - no selected conversation");
      return;
    }
    
    Logger.log("Sending reaction", { messageId, emoji });
    
    dispatch(sendReaction({
      messageId,
      emoji,
      phone: selectedConversation.mobile
    }))
      .unwrap()
      .then(() => {
        toast.success('Reaction sent');
      })
      .catch((error) => {
        Logger.log("Failed to send reaction:", error);
        toast.error('Failed to send reaction');
      });
  }, [dispatch, selectedConversation]);

  // Handle reply to messages with Redux
  const handleReply = useCallback((messageId: string) => {
    Logger.log("Setting reply context for message:", messageId);
    
    // Find the message in messagesByDate
    let messageToReply: any = null;
    
    // Search through all dates and times to find the message
    Object.keys(messagesByDate).forEach(date => {
      Object.keys(messagesByDate[date]).forEach(time => {
        const foundMsg = messagesByDate[date][time].find(
          msg => msg._id === messageId || msg.messageId === messageId
        );
        if (foundMsg) {
          messageToReply = foundMsg;
        }
      });
    });
    
    if (messageToReply) {
      Logger.log("Found message to reply to:", messageToReply._id);
      dispatch(setReplyContext(messageToReply));
      scrollToBottom();
    } else {
      Logger.log("Message to reply not found:", messageId);
    }
  }, [dispatch, messagesByDate, scrollToBottom]);

  // Check if a message has cached media
  const isMessageMediaCached = useCallback((messageId: string) => {
    return !!mediaCache[messageId];
  }, [mediaCache]);

  // Get cached media for a message
  const getCachedMedia = useCallback((messageId: string) => {
    return mediaCache[messageId] || null;
  }, [mediaCache]);

  // Format date for display
  const getFormattedTime = useCallback((date: string) => {
    const time = moment(date);
    const now = moment();
    
    if (time.format("L") === now.format("L")) {
      return "Today";
    }
    
    if (time.isSame(now.subtract(1, "days"), "day")) {
      return "Yesterday";
    }
    
    return time.format("MMMM D, YYYY");
  }, []);

  // User data memo
  const currentUser = useMemo(() => {
    return JSON.parse(
      localStorage.getItem("userData") || JSON.stringify({ firstName: "You" })
    );
  }, []);

  // Group message by time helper
  const groupedByTime = useCallback((messages: any[]) => {
    try {
      return messages.reduce((acc: any, item: any) => {
        const time = moment(item.createdAt).format("hh:mm a");
        if (!acc[time]) {
          acc[time] = [];
        }
        acc[time].push(item);
        return acc;
      }, {});
    } catch (error) {
      Logger.log("Error grouping messages by time:", error);
      return {};
    }
  }, []);

  // Empty chat state
  const renderEmptyChat = useCallback(() => {
    return (
      <div className="empty-chat">
        <div className="empty-chat-content">
          <h3>No conversation to show</h3>
          <p>
            Your inbox is empty. Messages will appear here when they arrive.
          </p>
        </div>
      </div>
    );
  }, []);

  const renderMessagesContent = useCallback(() => {
    if (JSON.stringify(messagesByDate) === "{}") {
      return renderEmptyChat();
    }
    
    // First, prepare all messages in a flat array
    const allMessages: Message[] = [];
    Object.keys(messagesByDate).forEach(date => {
      Object.keys(messagesByDate[date]).forEach(time => {
        messagesByDate[date][time].forEach(msg => {
          allMessages.push(msg);
        });
      });
    });
    
    // Find media groups
    const mediaGroups = findMediaGroups(allMessages);
    logger.log("IncomingMsgRedux - Detected media groups:", mediaGroups.length);
    
    // Create a set of message IDs that are part of groups
    const groupedMessageIds = new Set<string>();
    mediaGroups.forEach(group => {
      group.forEach(msg => {
        groupedMessageIds.add(msg._id);
      });
    });
    
    // Add this line - Keep track of which groups we've already rendered
    const renderedGroupIds = new Set<string>();
    
    return (
      <>
        {/* ✅ NEW: Loading indicator for older messages at TOP */}
        {loadingOlderMessages && (
          <div style={{ 
            textAlign: 'center', 
            padding: '16px',
            color: '#666',
            fontSize: '14px',
            background: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '8px',
            margin: '8px 16px'
          }}>
            <Spin size="small" />
            <span style={{ marginLeft: '8px' }}>Loading older messages...</span>
          </div>
        )}
        
        {/* ✅ NEW: "Beginning of conversation" indicator */}
        {!hasMoreMessages && allMessages.length > 0 && (
          <div style={{ 
            textAlign: 'center', 
            padding: '16px',
            color: '#999',
            fontSize: '12px',
            fontStyle: 'italic'
          }}>
            📅 Beginning of conversation
          </div>
        )}

        {Object.keys(messagesByDate).map((date: string, dateIndex: number) => {
          const timeGroups = groupedByTime(
            Object.keys(messagesByDate[date])
              .flatMap(time => messagesByDate[date][time])
          );
          
          return (
            <div key={`date-${dateIndex}`}>
              <Spin spinning={messagesStatus === 'loading'} size="large" tip="Loading...">
              <DateDivider date={date} />
                
                {Object.keys(timeGroups).map((timeKey, timeIndex) => {
                  // Process messages for this time group
                  const processedMessages: ReactNode[] = [];
                  let skipCounter = 0;
                  
                  timeGroups[timeKey].forEach((message: Message, msgIndex: number) => {
                    // If this message is part of a group and not the first one, skip it
                    if (skipCounter > 0) {
                      skipCounter--;
                      return;
                    }
                    
                    // Check if this message starts a group
                    if (groupedMessageIds.has(message._id)) {
                      // Find which group this message belongs to
                      const group = mediaGroups.find(g => g.some(m => m._id === message._id));
                      
                      if (group) {
                        // Generate a group ID
                        const groupId = getMediaGroupId(group);
                        
                        // Only render if we haven't rendered this group already
                        if (!renderedGroupIds.has(groupId)) {
                          renderedGroupIds.add(groupId);
                          logger.log("Adding media group:", groupId, "with", group.length, "items");
                          
                          processedMessages.push(
                            <div key={groupId} className="message-wrapper">
                              <MediaGroup messages={group} />
                            </div>
                          );
                        }
                        
                        // Skip the other messages in this group
                        skipCounter = group.length - 1;
                        return;
                      }
                    }
                    
                    // Regular message (not in a group)
                    const isMediaCached = isMessageMediaCached(message._id);
                    const cachedMedia = isMediaCached ? getCachedMedia(message._id) : null;
                    const isLoading = loadingMedia[message._id] || false;
                    const downloadError = downloadErrors[message._id] || "";
                    
                    const uniqueKey = `${
                      message._id ||
                      `${Date.now()}-${message.createdAt}-${msgIndex}`
                    }`;
                    
                    processedMessages.push(
                      <MessageBubbleRedux
                        key={uniqueKey}
                        message={message}
                        isFirstInGroup={msgIndex === 0}
                        isLastInGroup={msgIndex === timeGroups[timeKey].length - 1}
                        showTimestamp={true}
                      />
                    );
                  });
                  
                  return (
                    <div key={`time-${dateIndex}-${timeIndex}`} className="message-group">
                      {processedMessages}
                    </div>
                  );
                })}
              </Spin>
            </div>
          );
        })}
      </>
    );
  }, [
    messagesByDate, 
    messagesStatus, 
    groupedByTime, 
    renderEmptyChat,
    isMessageMediaCached,
    getCachedMedia,
    loadingMedia,
    downloadErrors,
    loadingOlderMessages, // ✅ NEW: Add to dependencies
    hasMoreMessages // ✅ NEW: Add to dependencies
  ]);

  return (
    <div 
      className="messages-container"
      ref={scrollRef} // ✅ NEW: Apply scroll ref here
    >
      {renderMessagesContent()}
      <div style={{ height: "50px" }}></div> {/* Add spacer at the bottom for better UX */}
    </div>
  );
};

export default IncomingMsgRedux;